import { FOULING_NEW_EVENT_BUTTON_ANCHOR_SELECTOR } from "@sop/FoulingPage/foulingTourTargets"
import { useTranslation } from "react-i18next"
import { Step } from "react-joyride"
import { CDN_URL } from "@utils/cdn"
import { CustomBodyMultiText, CustomBodyText, CustomHeader } from "../manualComponets"
import { ScrollRule } from "../UserManual"

export const FOULING_EVENT_LOG_BREAKPOINT = 1810
export const EFFICIENCY_ADDING_EVENTS_STEP_INDEX = 1

export function getEfficiencyScrollRules(windowWidth: number): {
	target: string
	opts: ScrollRule
}[] {
	void windowWidth

	return [
		{
			target: "#header-user-manual",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: -24,
					align: "end",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
		{
			target: FOULING_NEW_EVENT_BUTTON_ANCHOR_SELECTOR,
			opts: {
				// skip: true,
				containerSelector: "#body-layout",
				x: {
					offset: 0,
					align: "end",
				},
				y: {
					offset: -24,
					align: "end",
				},
			},
		},
		{
			target: "#filtering-data",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "end",
				},
				y: {
					offset: 10000,
					align: "start",
				},
			},
		},
		{
			target: "#efficiency-performance-plot",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "end",
				},
				y: {
					offset: 10000,
					align: "start",
				},
			},
		},
		{
			target: "#efficiency-trend-plot",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "end",
				},
				y: {
					offset: -24,
					align: "end",
				},
			},
		},
		{
			target: "#statistics-card",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "end",
				},
				y: {
					offset: 10000,
					align: "start",
				},
			},
		},
		{
			target: "#generate-report-button",
			opts: {
				containerSelector: "#body-layout",
				x: {
					offset: 24,
					align: "start",
				},
				y: {
					offset: 10000,
					align: "end",
				},
			},
		},
	]
}

export const efficiencySteps = (
	translationFunc?: ReturnType<typeof useTranslation>["t"],
	windowWidth = typeof window !== "undefined" ? window.innerWidth : FOULING_EVENT_LOG_BREAKPOINT
): Step[] => {
	const t = translationFunc ?? ((text: string) => text)
	const isWideLayout = windowWidth >= FOULING_EVENT_LOG_BREAKPOINT

	return [
		{
			target: "#header-user-manual",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.pageHeader.header")}</CustomHeader>
					<CustomBodyMultiText>
						<p className={`text-dipai-text-primary mb-1 px-2 text-justify`}>
							{t("efficiency.pageHeader.content")}
						</p>
						<p className={`text-dipai-text-primary mb-1 px-2 text-justify`}>
							{t("efficiency.pageHeader.content2")}
						</p>
						<p className={`text-dipai-text-primary px-2 text-justify`}>
							{t("efficiency.pageHeader.content3")}
						</p>
					</CustomBodyMultiText>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: FOULING_NEW_EVENT_BUTTON_ANCHOR_SELECTOR,
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.addingEvents.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.addingEvents.content")}</CustomBodyText>
				</div>
			),
			placement: isWideLayout ? "left" : "top-start",
			disableOverlay: !isWideLayout,
			disableScrollParentFix: !isWideLayout ? false : undefined,
		},
		{
			target: "#filtering-data",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.filteringData.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.filteringData.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
		{
			target: "#time-aggregation",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.timeAggregation.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.timeAggregation.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
		{
			target: "#efficiency-performance-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.propulsionPowerAnalysis.header")}</CustomHeader>
					<CustomBodyText>
						{t("efficiency.propulsionPowerAnalysis.content")}
					</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#efficiency-trend-plot",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.trendPlot.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.trendPlot.content")}</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#statistics-card",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.trendVariation.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.trendVariation.content")}</CustomBodyText>
				</div>
			),
			placement: "top",
		},
		{
			target: "#heatmap-tab-controls",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.otherAnalysis.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.otherAnalysis.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},
		{
			target: "#kgPerNm",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.fuelEfficiencyAnalysis.header")}</CustomHeader>
					<CustomBodyText>
						{t("efficiency.fuelEfficiencyAnalysis.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
		{
			target: "#tonnesPerDay",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.fuelConsumptionAnalysis.header")}</CustomHeader>
					<CustomBodyText>
						{t("efficiency.fuelConsumptionAnalysis.content")}
					</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
		{
			target: "#trim",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.trimAnalysis.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.trimAnalysis.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
		{
			target: "body",
			placement: "center",
			disableBeacon: true,
			content: (
				<div className="flex w-[80vw] flex-col">
					<CustomHeader>{t("efficiency.analyzeImpact.header")}</CustomHeader>
					<CustomBodyText>
						<span className={`mb-1`}>{t("efficiency.analyzeImpact.content")}</span>
						<span className={`mb-1`}>{t("efficiency.analyzeImpact.content2")}</span>
						<img
							src={`${CDN_URL}/userManual/efficiency/analyzeImpact.png`}
							alt="Analyze impact"
							className="mt-2 w-full rounded-lg border"
						/>
					</CustomBodyText>
				</div>
			),
		},
		{
			target: "body",
			placement: "center",
			disableBeacon: true,
			content: (
				<div className="flex w-[80vw] flex-col">
					<CustomHeader>{t("efficiency.impactAnalysisExample1.header")}</CustomHeader>
					<CustomBodyText>
						{t("efficiency.impactAnalysisExample1.content")}
						<img
							src={`${CDN_URL}/userManual/efficiency/impactAnalysisExample1.png`}
							alt="Impact analysis 1"
							className="mt-2 w-full rounded-lg border"
						/>
					</CustomBodyText>
				</div>
			),
		},
		{
			target: "body",
			placement: "center",
			disableBeacon: true,
			content: (
				<div className="flex w-[80vw] flex-col">
					<CustomHeader>{t("efficiency.impactAnalysisExample2.header")}</CustomHeader>
					<CustomBodyText>
						{t("efficiency.impactAnalysisExample2.content")}
						<img
							src={`${CDN_URL}/userManual/efficiency/impactAnalysisExample2.png`}
							alt="Impact analysis 2"
							className="mt-2 w-full rounded-lg border"
						/>
					</CustomBodyText>
				</div>
			),
		},
		{
			target: "body",
			placement: "center",
			disableBeacon: true,
			content: (
				<div className="flex w-[80vw] flex-col">
					<CustomHeader>{t("efficiency.impactAnalysisExample3.header")}</CustomHeader>
					<CustomBodyText>
						{t("efficiency.impactAnalysisExample3.content")}
						<img
							src={`${CDN_URL}/userManual/efficiency/impactAnalysisExample3.png`}
							alt="Impact analysis 3"
							className="mt-2 w-full rounded-lg border"
						/>
					</CustomBodyText>
				</div>
			),
		},
		{
			target: "#vessel-selector-card",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("consumption.vesselSelection.header")}</CustomHeader>
					<CustomBodyText>{t("consumption.vesselSelection.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom",
		},

		{
			target: "#generate-report-button",
			content: (
				<div className={`flex w-125 flex-col`}>
					<CustomHeader>{t("efficiency.generateReportButton.header")}</CustomHeader>
					<CustomBodyText>{t("efficiency.generateReportButton.content")}</CustomBodyText>
				</div>
			),
			placement: "bottom-start",
		},
	]
}
